import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Public hosted-pages base (/p/:principalId/:slug). Falls back to the API host,
// which serves /p/* on the same origin. In prod set to https://go.getadmin.ai
// if the public-pages domain is split from the API for deliverability reasons
// (see Plan.md §5.2 — separate sending reputation).
export const PUBLIC_PAGE_BASE_URL = process.env.REACT_APP_PUBLIC_PAGE_BASE_URL || BASE_URL;

// Create an axios instance. Auth token is injected per-request via getApiClient().
const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

async function buildFetchHeadersFromClient(client, extraHeaders = {}) {
  let config = { headers: {} };
  const handlers = Array.isArray(client?.interceptors?.request?.handlers)
    ? client.interceptors.request.handlers
    : [];
  for (const handler of handlers) {
    if (!handler || typeof handler.fulfilled !== 'function') continue;
    config = await handler.fulfilled(config);
  }
  const headers = {
    'Content-Type': 'application/json',
    ...(config.headers || {}),
    ...extraHeaders,
  };
  return headers;
}

async function streamPostSseJson(client, path, body, { onDelta, onComplete, onError }) {
  const headers = await buildFetchHeadersFromClient(client);
  const response = await fetch(`${BASE_URL}${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body || {}),
  });
  if (!response.ok || !response.body) {
    const text = await response.text().catch(() => '');
    throw new Error(text || `Request failed (${response.status})`);
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const processEvent = (rawEvent) => {
    const line = rawEvent
      .split('\n')
      .map((item) => item.trim())
      .find((item) => item.startsWith('data:'));
    if (!line) return;
    const payloadText = line.slice(5).trim();
    if (!payloadText) return;
    let payload;
    try {
      payload = JSON.parse(payloadText);
    } catch {
      return;
    }
    if (payload.type === 'delta') onDelta?.(String(payload.text || ''));
    else if (payload.type === 'complete') onComplete?.(payload);
    else if (payload.type === 'error') onError?.(new Error(payload.message || 'Streaming error'));
  };

  while (true) {
    const { value, done } = await reader.read();
    buffer += decoder.decode(value || new Uint8Array(), { stream: !done });
    let boundary = buffer.indexOf('\n\n');
    while (boundary >= 0) {
      const eventChunk = buffer.slice(0, boundary).replaceAll('\r', '');
      buffer = buffer.slice(boundary + 2);
      processEvent(eventChunk);
      boundary = buffer.indexOf('\n\n');
    }
    if (done) break;
  }
}

/**
 * Returns an axios instance configured with the Auth0 access token.
 * Call this inside components/hooks where you have access to getAccessTokenSilently.
 */
export function getApiClient(getAccessTokenSilently) {
  const client = axios.create({
    baseURL: BASE_URL,
    headers: { 'Content-Type': 'application/json' },
  });

  client.interceptors.request.use(async (config) => {
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        },
      });
      config.headers.Authorization = `Bearer ${token}`;
    } catch (err) {
      console.error('Failed to get access token:', err);
    }
    return config;
  });

  return client;
}

// ─── Leads ──────────────────────────────────────────────────────────────────

export async function fetchLeads(client, params = {}) {
  const { data } = await client.get('/api/leads', { params });
  return data;
}

export async function fetchLead(client, id) {
  const { data } = await client.get(`/api/leads/${id}`);
  return data;
}

export async function fetchLeadTasks(client, leadId) {
  const { data } = await client.get(`/api/leads/${leadId}/tasks`);
  return data;
}

export async function fetchLeadPlan(client, leadId) {
  const { data } = await client.get(`/api/leads/${leadId}/plan`);
  return data;
}

export async function patchTask(client, id, patch, etag) {
  const headers = etag ? { 'If-Match': etag } : undefined;
  const { data } = await client.patch(`/api/tasks/${id}`, patch, { headers });
  return data;
}

export async function fetchLeadEvents(client, leadId) {
  const { data } = await client.get(`/api/leads/${leadId}/events`);
  return data;
}

export async function createLead(client, payload) {
  const { data } = await client.post('/api/leads', payload);
  return data;
}

export async function updateLead(client, id, patch) {
  const { data } = await client.patch(`/api/leads/${id}`, patch);
  return data;
}

export async function deleteLead(client, id) {
  await client.delete(`/api/leads/${id}`);
}

export async function enrichLead(client, id) {
  const { data } = await client.post(`/api/leads/${id}/enrich`);
  return data;
}

export async function fetchActivities(client, leadId) {
  const { data } = await client.get(`/api/leads/${leadId}/activities`);
  return data;
}

export async function fetchOptOuts(client) {
  const { data } = await client.get('/api/v2/opt-outs');
  return data;
}

export async function logActivity(client, leadId, payload) {
  const { data } = await client.post(`/api/leads/${leadId}/activities`, payload);
  return data;
}

// ─── Principals / Cohorts ───────────────────────────────────────────────────

export async function fetchPrincipals(client) {
  const { data } = await client.get('/api/principals');
  return data;
}

export async function fetchMyPrincipal(client) {
  const { data } = await client.get('/api/principals/me');
  return data;
}

export async function updateMyPrincipal(client, patch) {
  const { data } = await client.patch('/api/principals/me', patch);
  return data;
}

export async function generateAgentPairingCode(client) {
  const { data } = await client.post('/api/principals/me/agent/pairing-code', {});
  return data;
}

export async function fetchAgentTokens(client) {
  const { data } = await client.get('/api/principals/me/agent/tokens');
  return Array.isArray(data?.tokens) ? data.tokens : [];
}

export async function revokeAgentToken(client, tokenId) {
  const { data } = await client.post(`/api/principals/me/agent/tokens/${encodeURIComponent(tokenId)}/revoke`, {});
  return data?.token || null;
}

export async function triggerWebsiteCrawlNow(client) {
  const { data } = await client.post('/api/principals/me/website/crawl-now', {});
  return data;
}

export async function uploadPrincipalPhoto(client, formData) {
  const { data } = await client.post('/api/principals/me/photo', formData, {
    headers: { 'Content-Type': undefined },
  });
  return data;
}

export async function uploadArticleImage(client, slug, formData) {
  const { data } = await client.post(`/api/v2/articles/${encodeURIComponent(slug)}/image`, formData, {
    headers: { 'Content-Type': undefined },
  });
  return data;
}

export async function acceptSendConsent(client) {
  const { data } = await client.patch('/api/principals/me', { consentAcceptedAt: true });
  return data;
}

export async function startLinkAccount(client, channel) {
  const { data } = await client.post('/api/unipile/link-account', { channel });
  return data;
}

export async function unlinkAccount(client, channel, accountId) {
  await client.delete(`/api/unipile/accounts/${encodeURIComponent(channel)}/${encodeURIComponent(accountId)}`);
}

export async function startUnipileLinkFlow(client, { channel }) {
  const { data } = await client.post('/api/unipile/link-account', { channel });
  return { authUrl: String(data?.url || ''), expiresAt: data?.expiresAt || null };
}

export async function unlinkUnipileAccount(client, { channel, accountId }) {
  await client.delete(`/api/unipile/accounts/${encodeURIComponent(channel)}/${encodeURIComponent(accountId)}`);
}

export async function fetchCohorts(client) {
  const { data } = await client.get('/api/cohorts');
  return data;
}

// ─── Campaigns ───────────────────────────────────────────────────────────────

export async function fetchCampaigns(client, opts = {}) {
  const params = {};
  if (opts.flow === 'coach' || opts.flow === 'business') params.flow = opts.flow;
  const { data } = await client.get('/api/campaigns', { params });
  return data;
}

export async function fetchCampaign(client, id) {
  const { data } = await client.get(`/api/campaigns/${id}`);
  return data;
}

export async function createCampaign(client, payload) {
  const { data } = await client.post('/api/campaigns', payload);
  return data;
}

export async function patchCampaign(client, id, patch) {
  const { data } = await client.patch(`/api/campaigns/${id}`, patch);
  return data;
}

export async function patchPhase(client, campaignId, id, patch) {
  const { data } = await client.patch(`/api/campaigns/${campaignId}/phases/${id}`, patch);
  return data;
}

export async function patchTouch(client, campaignId, id, patch) {
  const { data } = await client.patch(`/api/campaigns/${campaignId}/touches/${id}`, patch);
  return data;
}

export async function createTouch(client, campaignId, body) {
  const { data } = await client.post(`/api/campaigns/${campaignId}/touches`, body);
  return data;
}

export async function deleteTouch(client, campaignId, id) {
  await client.delete(`/api/campaigns/${campaignId}/touches/${id}`);
}

export async function deleteCampaign(client, id) {
  await client.delete(`/api/campaigns/${id}`);
}

export async function duplicateCampaign(client, id) {
  const { data } = await client.post(`/api/campaigns/${id}/duplicate`);
  return data?.campaign || data;
}

export async function regenerateCampaignDrafts(client, id) {
  const { data } = await client.post(`/api/campaigns/${id}/regenerate-drafts`);
  return data;
}

// ─── Campaign generator (beta) ───────────────────────────────────────────────

export async function fetchFlags(client) {
  const { data } = await client.get('/api/flags');
  return data;
}

export async function generateCampaignFlow(client, payload) {
  // Reasoning models can take several minutes on a complex brief — match the
  // other generator calls (10 min) instead of the old 2 min that timed out.
  const { data } = await client.post('/api/campaigns/generate-flow', payload, { timeout: 600000 });
  return data;
}

export async function generateCampaignMessages(client, id) {
  // One LLM call for all touch messages — reasoning models can take minutes.
  const { data } = await client.post(`/api/campaigns/${id}/generate-messages`, {}, { timeout: 600000 });
  return data;
}

export async function generateTouchPage(client, campaignId, touchId, stylingUrl) {
  const { data } = await client.post(
    `/api/campaigns/${campaignId}/generate-page`,
    { touchId, stylingUrl: stylingUrl || undefined },
    { timeout: 600000 }
  );
  return data;
}

export async function discoverProspects(client, campaignId, targeting) {
  const { data } = await client.post(
    `/api/campaigns/${campaignId}/discover-prospects`,
    { targeting },
    { timeout: 60000 }
  );
  return data;
}

export async function importProspects(client, campaignId, prospects) {
  const { data } = await client.post(
    `/api/campaigns/${campaignId}/import-prospects`,
    { prospects },
    { timeout: 120000 }
  );
  return data;
}

export async function fetchStats(client) {
  const { data } = await client.get('/api/stats');
  return data;
}

export async function fetchEvents(client) {
  const { data } = await client.get('/api/events');
  return data;
}

// ─── Hosted Pages Analytics ──────────────────────────────────────────────────

export async function fetchPageAnalytics(client) {
  const { data } = await client.get('/api/pages/analytics');
  return data;
}

export async function fetchPageAnalyticsDetail(client, slug, range = '30d') {
  const { data } = await client.get(`/api/pages/analytics/${slug}`, { params: { range } });
  return data;
}

export async function fetchHostedPages(client) {
  const { data } = await client.get('/api/pages');
  return data;
}

export async function createHostedPage(client, payload) {
  const { data } = await client.post('/api/pages', payload);
  return data;
}

export async function resolveExternalArticleLink(client, url) {
  const { data } = await client.post('/api/pages/resolve-link', { url }, { timeout: 10000 });
  return data;
}

export async function uploadHostedPage(client, file, slug) {
  const formData = new FormData();
  formData.append('file', file);
  // Passing an existing slug replaces that page's content in place (stats kept).
  if (slug) formData.append('slug', slug);
  // Clear the instance-level Content-Type so the browser sets multipart/form-data + boundary.
  const { data } = await client.post('/api/pages/upload', formData, {
    headers: { 'Content-Type': undefined },
  });
  return data;
}

export async function deleteHostedPage(client, slug) {
  await client.delete(`/api/pages/${slug}`);
}

export async function resetPageAnalytics(client, slug) {
  await client.delete(`/api/pages/analytics/${slug}/visits`);
}

// ─── Aviary v2 flows ─────────────────────────────────────────────────────────

export async function fetchV2Flows(client) {
  const { data } = await client.get('/api/v2/flows');
  return data.flows || [];
}

export async function fetchV2Flow(client, id) {
  const { data } = await client.get(`/api/v2/flows/${id}`);
  return data;
}

export async function fetchV2Destinations(client) {
  const { data } = await client.get('/api/v2/destinations');
  return data;
}

export async function discoverV2Campaign(client, { flowId, answers, campaignId }) {
  // Skeleton + strategy note only. No prospect search, no message gen — those
  // are progressive follow-up calls. Still allow a long timeout because the
  // skeleton LLM call itself can be slow on reasoning models.
  const { data } = await client.post(
    '/api/v2/campaigns/discover',
    { flowId, answers, campaignId },
    { timeout: 600000 }
  );
  return data;
}

export async function fetchPriorityCompanySuggestions(client, { industries, locations }) {
  const { data } = await client.post(
    '/api/v2/campaigns/priority-company-suggestions',
    { industries, locations },
    { timeout: 60000 }
  );
  return data;
}

export async function resolveV2Company(client, name) {
  const { data } = await client.post(
    '/api/v2/campaigns/resolve-company',
    { name },
    { timeout: 30000 }
  );
  return data;
}

export async function writeV2CampaignMessages(client, campaignId) {
  const { data } = await client.post(
    `/api/v2/campaigns/${campaignId}/write-messages`,
    {},
    { timeout: 600000 }
  );
  return data;
}

export async function writeV2CampaignMessage(client, campaignId, index) {
  // One touch at a time — fire N of these in parallel from the UI.
  const { data } = await client.post(
    `/api/v2/campaigns/${campaignId}/write-message`,
    { index },
    { timeout: 600000 }
  );
  return data;
}

export async function findV2CampaignProspects(client, campaignId, mode) {
  const suffix = mode ? `?mode=${encodeURIComponent(mode)}` : '';
  const { data } = await client.post(
    `/api/v2/campaigns/${campaignId}/find-prospects${suffix}`,
    {},
    { timeout: 600000 }
  );
  return data;
}

export async function approveV2Campaign(client, campaignId, { selectedProspects }) {
  const { data } = await client.post(
    `/api/v2/campaigns/${campaignId}/approve`,
    { selectedProspects },
    { timeout: 120000 }
  );
  return data;
}

// "Find more" top-up: re-runs discovery on an active campaign, hard-filters
// existing leads across the whole principal, stores the batch on
// campaign.proposedProspects for the review page.
export async function findMoreProspects(client, campaignId) {
  const { data } = await client.post(
    `/api/v2/campaigns/${campaignId}/find-prospects?mode=topup`,
    {},
    { timeout: 600000 }
  );
  return data;
}

// Add reviewed candidates from a Find-more batch to an already-active campaign.
// Does NOT touch campaign status.
export async function addProspectsToCampaign(client, campaignId, { selectedProspects }) {
  const { data } = await client.post(
    `/api/v2/campaigns/${campaignId}/add-prospects`,
    { selectedProspects },
    { timeout: 120000 }
  );
  return data;
}

// Backs the "+ Add a prospect" tile on the pipeline board. Server runs Apollo
// people/match on the URL, personalises, drops the lead in as a prospect
// awaiting approval.
export async function addProspectByUrl(client, campaignId, linkedinUrl) {
  const { data } = await client.post(
    `/api/v2/campaigns/${campaignId}/add-prospect-by-url`,
    { linkedinUrl },
    { timeout: 120000 }
  );
  return data;
}

// ─── Articles (v2) ───────────────────────────────────────────────────────────
// Articles reuse the hostedPages container with kind='article'. The routes are
// the existing /api/pages endpoints; these wrappers just pin the kind filter.

export async function fetchArticles(client) {
  const { data } = await client.get('/api/pages', { params: { kind: 'article' } });
  return Array.isArray(data?.pages) ? data.pages : [];
}

export async function fetchArticle(client, slug) {
  const { data } = await client.get(`/api/pages/${slug}`);
  return data;
}

export async function cocreateArticle(client, { topic, notes, campaignId, templateSlug }) {
  const { data } = await client.post(
    '/api/v2/articles/cocreate',
    {
      topic,
      notes: notes || '',
      campaignId: campaignId || undefined,
      templateSlug: templateSlug || undefined,
    },
    { timeout: 120000 }
  );
  return data;
}

export async function fetchArticleTemplates(client) {
  const { data } = await client.get('/api/v2/articles/templates');
  return Array.isArray(data?.templates) ? data.templates : [];
}

export async function fetchArticlePreview(client, slug) {
  const { data } = await client.get(`/api/v2/articles/${encodeURIComponent(slug)}/preview`, {
    responseType: 'text',
  });
  return data;
}

export async function updateArticleTemplate(client, slug, templateSlug) {
  const { data } = await client.patch(`/api/v2/articles/${encodeURIComponent(slug)}/template`, { templateSlug });
  return data;
}

export async function streamCowrite(client, slug, body, { onDelta, onComplete, onError } = {}) {
  await streamPostSseJson(
    client,
    `/api/v2/articles/${encodeURIComponent(slug)}/cowrite/stream`,
    body,
    { onDelta, onComplete, onError }
  );
}

// ─── Knowledge Base (v2) ─────────────────────────────────────────────────────
export async function fetchKnowledgeBaseAssets(client) {
  const res = await client.get('/api/v2/agent/assets?limit=200&includeText=false');
  return res.data?.assets || [];
}

export async function streamRewriteSelection(client, slug, body, { onDelta, onComplete, onError } = {}) {
  await streamPostSseJson(
    client,
    `/api/v2/articles/${encodeURIComponent(slug)}/cowrite/rewrite-selection`,
    body,
    { onDelta, onComplete, onError }
  );
}

export async function patchCowriteCanvas(client, slug, canvasHtml) {
  const { data } = await client.patch(
    `/api/v2/articles/${encodeURIComponent(slug)}/cowrite/canvas`,
    { canvasHtml }
  );
  return data;
}

// Upload an article HTML/zip file. `slug` is optional (server auto-slugifies from title).
export async function uploadArticle(client, file, { slug, title, articleStatus = 'draft', originNote = 'Uploaded HTML' } = {}) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('kind', 'article');
  formData.append('articleStatus', articleStatus);
  formData.append('originNote', originNote);
  if (slug) formData.append('slug', slug);
  if (title) formData.append('title', title);
  const { data } = await client.post('/api/pages/upload', formData, {
    headers: { 'Content-Type': undefined },
    timeout: 60000,
  });
  return data;
}

export async function updateArticle(client, slug, patch) {
  const { data } = await client.patch(`/api/pages/${slug}`, patch);
  return data;
}

export async function deleteArticle(client, slug) {
  await client.delete(`/api/pages/${slug}`);
}

// Segment recipient counts for an article promote flow.
// segments: ['base', 'cold', 'active', 'new'] — omit to fetch all four.
export async function fetchArticleSegmentCounts(client, slug, segments) {
  const { data } = await client.post(
    `/api/v2/articles/${slug}/segment-counts`,
    { segments },
    { timeout: 60000 }
  );
  return data;
}

export async function fetchArticleConnectedCount(client, slug) {
  const { data } = await client.get(`/api/v2/articles/${slug}/connected-count`, { timeout: 60000 });
  return data;
}

// Kick off a promotion. Mints per-recipient magic-link linkIds, LLM-personalizes
// message per recipient, drops each into lead.articleDrafts (approval queue).
export async function promoteArticle(client, slug) {
  const { data } = await client.post(
    `/api/v2/articles/${slug}/promote`,
    {},
    { timeout: 600000 } // LLM per recipient, can be minutes for large lists
  );
  return data;
}

// Analytics for an article: stat tiles + who-clicked list + pending reactivation count.
export async function fetchArticleAnalytics(client, slug) {
  const { data } = await client.get(`/api/v2/articles/${slug}/analytics`);
  return data;
}

export async function generateShareMessage(client, payload) {
  const { data } = await client.post('/api/v2/pipeline/share/generate-message', payload, {
    timeout: 60000,
  });
  return data;
}

export async function shareArticleWithLead(client, leadId, articleSlug) {
  const { data } = await client.post(
    '/api/v2/pipeline/share/generate-message',
    { leadId, articleSlug },
    { timeout: 60000 }
  );
  return data;
}

export async function sendArticleShare(client, payload) {
  const { data } = await client.post('/api/v2/pipeline/share/send', payload, {
    timeout: 30000,
  });
  return data;
}

export async function ensureV2TestLinks(client, campaignId) {
  const { data } = await client.post(
    `/api/v2/campaigns/${campaignId}/test-links`,
    {},
    { timeout: 30000 }
  );
  return data.links || {};
}

export async function generateTesterIntro(client, campaignId, { rawProfile, firstName } = {}) {
  const { data } = await client.post(
    `/api/v2/campaigns/${campaignId}/tester-intro`,
    { rawProfile, firstName },
    { timeout: 30000 }
  );
  return String(data?.intro || '');
}

export async function fetchProspectAutoStatus(client) {
  const { data } = await client.get('/api/v2/prospect-auto/status');
  return data;
}

export async function getConnectedAutoStatus(client) {
  const { data } = await client.get('/api/v2/connected-auto/status');
  return data;
}

export async function pingConnectedAutoNextTask(client) {
  const { data } = await client.post('/api/v2/connected-auto/next-task', {});
  return data;
}

export async function resetConnectedAutoFailures(client) {
  const { data } = await client.post('/api/v2/connected-auto/reset-failures', {});
  return data;
}

export async function generateIntroDraft(client, leadId, { rewrite = false } = {}) {
  const query = rewrite ? '?rewrite=true' : '';
  const { data } = await client.post(
    `/api/v2/leads/${encodeURIComponent(leadId)}/intro-draft${query}`,
    {},
    { timeout: 60000 }
  );
  return data;
}

export async function postConnectedAutoConsent(client) {
  const { data } = await client.post('/api/v2/connected-auto/consent', {});
  return data;
}

export async function fetchLatestExtension(client) {
  const { data } = await client.get('/api/v2/extension/latest');
  return data;
}

export async function triggerOrganicBackfill(client) {
  const { data } = await client.post('/api/v2/organic-connects/backfill', {});
  return data;
}

export async function importLeadsFromCsv(client, campaignId, file) {
  const form = new FormData();
  form.append('file', file);
  const { data } = await client.post(
    `/api/v2/campaigns/${campaignId}/leads/import`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
}

export default apiClient;
